import { Button, Col, Row, Spin, Card, Tooltip } from "antd";
import "./Dasboard.css";
import TotalIcon from "../../assets/Icon1.svg";
import LiveIcon from "../../assets/Icon2.svg";
import InprogressIcon from "../../assets/Icon3.svg";
import ClosedIcon from "../../assets/Icon4.svg";
import Sidebar from "../Sidebar/Sidebar";
import Header from "../header/Header";
import GreetingBanner from "./GreetingBanner";
import { useEffect, useRef, useState } from "react";
import {
  getAllRequest,
  getAllNotifications,
  getRequestCount,
} from "../../services/ApiService";
import UpcomingEvents from "./UpcomingEvents";
import { decryptObject } from "../GlobalFunction";
import { useNavigate } from "react-router-dom";

const AnimatedNumber = ({ value = 0, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  // Count up from the number currently on screen (0 only on first load), so a later
  // refresh of the figures changes them quietly instead of replaying from 0.
  const shown = useRef(0);

  useEffect(() => {
    const target = Number(value) || 0;
    const from = shown.current;
    let frame;
    let startTime = null;

    const step = (timestamp) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const current = Math.floor(from + (target - from) * progress);
      shown.current = current;
      setDisplay(current);
      if (progress < 1) {
        frame = requestAnimationFrame(step);
      } else {
        shown.current = target;
        setDisplay(target);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, duration]);

  return <>{display}</>;
};

const StatusBreakdown = ({ stats }) => {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const segments = [
    { key: "live", label: "Live", value: stats?.live || 0, color: "var(--series-1)" },
    {
      key: "onboarding",
      label: "Onboarding",
      value: stats?.onboarding || 0,
      color: "var(--series-2)",
    },
    { key: "exit", label: "Exit", value: stats?.exit || 0, color: "var(--series-3)" },
  ];

  const total = segments.reduce((sum, s) => sum + s.value, 0);

  return (
    <div className="viz-root">
      <div className="stacked-bar-track">
        {total === 0 ? (
          <div className="stacked-bar-empty" />
        ) : (
          segments.map((s) => {
            const pct = (s.value / total) * 100;
            return (
              <Tooltip
                key={s.key}
                title={`${s.label}: ${s.value} (${pct.toFixed(1)}%)`}
              >
                <div
                  className="stacked-bar-segment"
                  style={{
                    background: s.color,
                    flexBasis: animated ? `${pct}%` : "0%",
                  }}
                />
              </Tooltip>
            );
          })
        )}
      </div>

      <div className="stacked-bar-legend">
        {segments.map((s) => (
          <div className="legend-item" key={s.key}>
            <span className="legend-swatch" style={{ background: s.color }} />
            <span className="legend-label">{s.label}</span>
            <span className="legend-count">{s.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const NotificationsFeed = ({ notifications, onViewAll }) => {
  if (!notifications || notifications.length === 0) {
    return <div className="activity-feed-empty">No notifications yet.</div>;
  }

  return (
    <div className="activity-feed">
      {notifications.map((item) => (
        <div className="activity-feed-row" key={item.id}>
          <div className="activity-feed-main">
            <span className="activity-feed-name">{item.title}</span>
            <span className="activity-feed-by">{item.message}</span>
          </div>
          <span className="activity-feed-date">
            {item.created_date}
          </span>
        </div>
      ))}
      <Button type="link" className="activity-feed-viewall" onClick={onViewAll}>
        View all notifications →
      </Button>
    </div>
  );
};

const MONTH_ABBR = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

// Parses the backend's "DD-MMM-YY" date format, e.g. "11-Nov-25"
const parseDDMMMYY = (str) => {
  if (!str) return null;
  const parts = str.split("-");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const monthIdx = MONTH_ABBR[parts[1]];
  let year = parseInt(parts[2], 10);
  if (isNaN(day) || monthIdx === undefined || isNaN(year)) return null;
  year = year < 100 ? 2000 + year : year;
  return new Date(year, monthIdx, day);
};

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const OnboardingMonthComparison = ({ currentCount, lastCount, currentLabel, lastLabel }) => {
  const delta = currentCount - lastCount;
  const isUp = delta > 0;
  const pctChange = lastCount > 0 ? Math.round((Math.abs(delta) / lastCount) * 100) : null;

  return (
    <div className="month-compare">
      <div className="month-compare-tile muted">
        <span className="month-compare-label">{lastLabel}</span>
        <span className="month-compare-value">
          <AnimatedNumber value={lastCount} />
        </span>
      </div>

      <div className="month-compare-arrow">→</div>

      <div className="month-compare-tile">
        <span className="month-compare-label">{currentLabel}</span>
        <span className="month-compare-value">
          <AnimatedNumber value={currentCount} />
        </span>
      </div>

      {delta !== 0 && (
        <span className={`month-compare-delta ${isUp ? "up" : "down"}`}>
          {isUp ? "▲" : "▼"} {pctChange !== null ? `${pctChange}%` : Math.abs(delta)}
        </span>
      )}
      {delta === 0 && <span className="month-compare-delta flat">No change</span>}
    </div>
  );
};

const Dashboard = () => {
  const sessionData = decryptObject(sessionStorage.getItem("sessioninfo"));
  const name = sessionData.username;
  const isAdmin = String(sessionData?.userType) === "1";
  const [stats, setStats] = useState({});
  const [portfolioStats, setPortfolioStats] = useState({
    live: 0,
    onboarding: 0,
    exit: 0,
    totalRequest: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loader, setLoader] = useState(true);
  const [quote, setQuote] = useState();
  const [author, setAuthor] = useState();
  // The "Upcoming & Overdue (next 30 days)" list loads and pages itself; the greeting banner
  // only needs to know how many onboardings are coming up.
  const [upcomingOnboardCount, setUpcomingOnboardCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onboardingMonthStats, setOnboardingMonthStats] = useState({
    current: 0,
    last: 0,
    currentLabel: "",
    lastLabel: "",
  });
  const navigate = useNavigate();

  const fetchQuote = async () => {
    setLoader(true);
    try {
      const res = await fetch("https://dummyjson.com/quotes/random");
      const data = await res.json();
      setQuote(data.quote);
      setAuthor(data.author);
    } catch (err) {
      setQuote("Could not fetch quote.");
    } finally {
      setLoader(false);
    }
  };

  const fetchCompanyStats = async () => {
    try {
      const res = await getRequestCount();
      if (res.responseCode === 200) {
        setStats(res.data);
      }
    } catch (error) {
      console.error("Error fetching company-wide stats:", error);
    }
  };

  // silent = refresh the figures in the background, without the full-page loading overlay
  const fetchMyPortfolio = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      // A non-admin only needs their own records, so ask the server for just those
      // (otherwise they are picked out of the newest 1000 rows and older ones are missed).
      const res = await getAllRequest(
        1,
        1000,
        null,
        isAdmin ? {} : { account_owner: name }
      );
      const allRecords = res?.onboard_forms || [];
      const myRecords = isAdmin
        ? allRecords
        : allRecords.filter((record) => record.Account_Owner === name);

      // Portfolio status breakdown + total.
      // Admin: use the very same counts as the tiles above (company-wide, over ALL
      // records) -- counting from the 1000-row page above would only cover the newest
      // 1000 records and never match the tiles.
      if (isAdmin) {
        const counts = await getRequestCount();
        if (counts?.responseCode === 200) {
          setPortfolioStats({
            live: counts.data.live || 0,
            onboarding: counts.data.onboarding || 0,
            exit: counts.data.exit || 0,
            totalRequest: counts.data.totalRequest || 0,
          });
        }
      } else {
        setPortfolioStats({
          live: myRecords.filter((r) => r.Employee_Status === "Live").length,
          onboarding: myRecords.filter((r) => r.Employee_Status === "Onboarding")
            .length,
          exit: myRecords.filter((r) => r.Employee_Status === "Exit").length,
          totalRequest: myRecords.length,
        });
      }

      // Onboarding volume this month vs last, by Start Date of Employment.
      // Not gated by current status, so the "last month" figure stays a
      // stable historical fact instead of eroding as records change status.
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      let lastMonth = currentMonth - 1;
      let lastMonthYear = currentYear;
      if (lastMonth < 0) {
        lastMonth = 11;
        lastMonthYear -= 1;
      }

      let currentCount = 0;
      let lastCount = 0;
      const withStartDate = myRecords.map((r) => ({
        ...r,
        _startDate: parseDDMMMYY(r.Start_date_of_Employment),
      }));

      withStartDate.forEach((r) => {
        if (!r._startDate) return;
        if (
          r._startDate.getFullYear() === currentYear &&
          r._startDate.getMonth() === currentMonth
        ) {
          currentCount += 1;
        } else if (
          r._startDate.getFullYear() === lastMonthYear &&
          r._startDate.getMonth() === lastMonth
        ) {
          lastCount += 1;
        }
      });

      setOnboardingMonthStats({
        current: currentCount,
        last: lastCount,
        currentLabel: MONTH_NAMES[currentMonth],
        lastLabel: MONTH_NAMES[lastMonth],
      });

    } catch (error) {
      console.error("Error fetching portfolio data:", error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // A status was changed from the Upcoming & Overdue list. That list reloads itself; here the
  // tile / breakdown numbers above are updated quietly in the background (no page-wide
  // spinner) so they never go stale.
  const refreshAfterStatusChange = () => {
    fetchCompanyStats();
    fetchMyPortfolio(true);
  };

  const fetchMyNotifications = async () => {
    try {
      const res = await getAllNotifications();
      if (res?.responseCode === 200) {
        const allNotifications = res?.data || [];
        setNotifications(allNotifications.slice(0, 5));
        setUnreadCount(allNotifications.filter((n) => n.status === 1).length);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  useEffect(() => {
    // if (!hasFetchedQuote.current) {
    fetchQuote();
    fetchCompanyStats();
    fetchMyPortfolio();
    fetchMyNotifications();
    //   hasFetchedQuote.current = true;
    // }
  }, []);

  const handleCardClick = (statusValue) => {
    const url = statusValue
      ? `/employee-list?status=${statusValue}` // e.g., /employee-list?status=live
      : "/employee-list"; // All employees if no status

    navigate(url);
  };

  return (
    <>
      <div className="main-box">
        <Sidebar activeId={1} />
        <main className="main-content">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              flex: 1,
              overflow: "hidden",
            }}
          >
            <Header title="Home" />
            <div
              className="responsiveContainer"
              style={{ padding: 16, flex: 1, minHeight: 0, overflowY: "auto" }}
            >
              <Spin spinning={loading || loader} tip="Loading..." size="large">
              <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={24}>
                    <GreetingBanner
                      name={name}
                      portfolioStats={isAdmin ? null : portfolioStats}
                      upcomingCount={upcomingOnboardCount}
                      unreadCount={unreadCount}
                      onNavigate={handleCardClick}
                      onOpenNotifications={() => navigate("/notification")}
                    />
                    {/* <Card
                      className="onBoardBox"
                      bordered
                      hoverable
                      style={{ marginTop: "1.3rem" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <h3 className="obboard-name">Hello {name} !</h3>
                        <img
                          src={require("../../assets/hi.gif")}
                          alt="Greeting GIF"
                          style={{ width: 100, height: 100 }}
                        />
                      </div>

                      <span
                        style={{ fontFamily: "Baloo Bhai", color: "#514F6E" }}
                      >
                        {quote}
                      </span>

                      <div style={{ textAlign: "right", marginTop: "0.5rem" }}>
                        <span
                          style={{ fontFamily: "Baloo Bhai", color: "#514F6E" }}
                        >
                          ~{author}
                        </span>
                      </div>
                    </Card> */}
                  </Col>
                </Row>
                <Row gutter={[16, 16]}>
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Card
                      className="stat-card fade-in-up"
                      style={{ animationDelay: "0ms" }}
                      title={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.4rem",
                          }}
                        >
                          <img
                            src={LiveIcon}
                            alt="Live Icon"
                            style={{ width: 35, height: 35 }}
                          />
                          Live
                        </span>
                      }
                      bordered
                      hoverable
                      onClick={() => handleCardClick("live")}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "1.5rem",
                          fontWeight: "500",
                        }}
                      >
                        <AnimatedNumber value={stats?.live || 0} />
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Card
                      className="stat-card fade-in-up"
                      style={{ animationDelay: "80ms" }}
                      title={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.4rem",
                          }}
                        >
                          <img
                            src={InprogressIcon}
                            alt="In Progress Icon"
                            style={{ width: 35, height: 35 }}
                          />
                          Onboarding
                        </span>
                      }
                      bordered
                      hoverable
                      onClick={() => handleCardClick("onboarding")}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "1.5rem",
                          fontWeight: "500",
                        }}
                      >
                        <AnimatedNumber value={stats?.onboarding || 0} />
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Card
                      className="stat-card fade-in-up"
                      style={{ animationDelay: "160ms" }}
                      title={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.4rem",
                          }}
                        >
                          <img
                            src={ClosedIcon}
                            alt="Lost/Closed"
                            style={{ width: 35, height: 35 }}
                          />
                          Exit
                        </span>
                      }
                      bordered
                      hoverable
                      onClick={() => handleCardClick("exit")}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "1.5rem",
                          fontWeight: "500",
                        }}
                      >
                        <AnimatedNumber value={stats?.exit || 0} />
                      </div>
                    </Card>
                  </Col>
                  <Col xs={12} sm={12} md={6} lg={6}>
                    <Card
                      className="stat-card fade-in-up"
                      style={{ animationDelay: "240ms" }}
                      title={
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1.4rem",
                          }}
                        >
                          <img
                            src={TotalIcon}
                            alt="Total"
                            style={{ width: 35, height: 35 }}
                          />
                          Total
                        </span>
                      }
                      bordered
                      hoverable
                    >
                      <div
                        style={{
                          textAlign: "center",
                          fontSize: "1.5rem",
                          fontWeight: "500",
                        }}
                      >
                        <AnimatedNumber value={stats?.totalRequest || 0} />
                      </div>
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={14}>
                    <Card
                      className="fade-in-up"
                      style={{ animationDelay: "320ms" }}
                      bordered
                      title="Status Breakdown"
                    >
                      <StatusBreakdown stats={portfolioStats} />
                    </Card>
                  </Col>
                  <Col xs={24} md={10}>
                    <Card
                      className="fade-in-up"
                      style={{ animationDelay: "360ms" }}
                      bordered
                      title={
                        isAdmin
                          ? "Onboarding: Month over Month"
                          : "My Onboarding: Month over Month"
                      }
                    >
                      <OnboardingMonthComparison
                        currentCount={onboardingMonthStats.current}
                        lastCount={onboardingMonthStats.last}
                        currentLabel={onboardingMonthStats.currentLabel}
                        lastLabel={onboardingMonthStats.lastLabel}
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={[16, 16]}>
                  <Col xs={24} md={14}>
                    <Card
                      className="fade-in-up"
                      style={{ animationDelay: "400ms" }}
                      bordered
                      title="Upcoming & Overdue (Next 30 Days)"
                    >
                      <UpcomingEvents
                        onChanged={refreshAfterStatusChange}
                        onCounts={(counts) =>
                          setUpcomingOnboardCount(counts?.current_onboard || 0)
                        }
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={10}>
                    <Card
                      className="fade-in-up"
                      style={{ animationDelay: "480ms" }}
                      bordered
                      title="My Notifications"
                    >
                      <NotificationsFeed
                        notifications={notifications}
                        onViewAll={() => navigate("/notification")}
                      />
                    </Card>
                  </Col>
                </Row>
              </div>
              </Spin>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Dashboard;
